import React, {useMemo} from 'react';
import styled from 'styled-components';
import {useTranslations} from "use-intl";

import {ThumbnailType} from '@tager/web-modules';
import {Nullable} from '@tager/web-core';

import Collapse from '@/modules/Product/components/Main/components/Tabs/components/Collapse';
import {colors} from '@/constants/theme';
import {FullProductType, ProductParamsValueType,} from '@/typings/model';
import {TFunction} from "@/typings/common";
import {getCurrentProject} from "@/utils/common";

export type ParamsType = {
  label: string;
  value:
    | string
    | number
    | null
    | boolean
    | ProductParamsValueType
    | Array<ProductParamsValueType>;
  to?: string;
  category?: string;
  type?: string;
  multiple?: boolean;
};

export type DescriptionType = {
  content: string;
};

export type SchemaType = {
  original: ThumbnailType;
  preview: ThumbnailType;
};

function getMatressSizeLabel(t: TFunction, type: string) {
  switch (type) {
    case 'DLINA-SPALNOGO-MESTA':
      return t('params.param_6');
    case 'SHIRINA-SPALNOGO-MESTA':
      return t('params.param_9');
    default:
      return '';
  }
}

type Props = {
  product: Nullable<FullProductType>;
};

function Tabs({product}: Props) {
  const t = useTranslations();

  const dataList = useMemo(() => {
    let params: ParamsType[] = [];
    const description: DescriptionType[] = [];
    const schema: SchemaType[] = [];

    if (product?.params) {
      product.params.forEach((param, index) => {
        if (param.field.name === 'MEHANIZM-TRANSFORMACII') {
          params.push({
            label: param.field.label,
            value: (param.value as ProductParamsValueType)?.label,
            to: '/blog/mehanizmy-razlojeniya-divanov/',
            category: param.field.name,
            type: param.field.type,
          });
        }
        if (
          param.field.name === 'DLINA' ||
          param.field.name === 'VYSOTA' ||
          param.field.name === 'GLUBINA'
        ) {
          params.push({
            label: param.field.label,
            value: Math.ceil((param.value as number) / 10) + ' ' + t('dimensions.sm'),
            category: 'size',
            type: param.field.name,
          });
        }
        if (
          param.field.name === 'DLINA-SPALNOGO-MESTA' ||
          param.field.name === 'SHIRINA-SPALNOGO-MESTA'
        ) {
          params.push({
            label: getMatressSizeLabel(t, param.field.name),
            value: Math.ceil((param.value as number) / 10) + ' ' + t('dimensions.sm'),
            category: 'sleeping-size',
            type: param.field.name,
          });
        }
        if (
          param.field.type === 'NUMBER' &&
          param.field.name !== 'DLINA' &&
          param.field.name !== 'GLUBINA' &&
          param.field.name !== 'VYSOTA' &&
          param.field.name !== 'DLINA-SPALNOGO-MESTA' &&
          param.field.name !== 'SHIRINA-SPALNOGO-MESTA'
        ) {
          params.push({
            label: param.field.label,
            value: param.value,
            type: param.field.type,
          });
        }
        if (
          param.field.type === 'SELECT' &&
          param.field.name !== 'MEHANIZM-TRANSFORMACII' &&
          param.field.name !== 'KOMPLEKTACIYA'
        ) {
          params.push({
            label: param.field.label,
            value: param.field.multiple
              ? (param.value as ProductParamsValueType[])
              : param.value !== null
                ? (param.value as ProductParamsValueType)
                : t('dimensions.no'),
            multiple: param.field.multiple,
            type: param.field.type,
          });
        }
        if (param.field.type === 'TRUE_FALSE') {
          params.push({
            type: param.field.type,
            label: param.field.label,
            value: param.value ? t('dimensions.yes') : t('dimensions.no'),
          });
        }
      });
    }

    if (getCurrentProject() === 'dveri') {
      product?.params.forEach(productParam => {

        params.push({
          type: "STRING",
          label: productParam.field.label.toString(),
          value: productParam.value ? productParam.value.toString() : ''
        });
      })
    }

    console.log(params);

    if (product?.content) {
      description.push({
        content: product.content,
      });
    }

    if (product?.schemaImage) {
      schema.push({
        original: product.schemaImage.original,
        preview: product.schemaImage.preview,
      });
    }

    return [
      {type: 'params', label: t('product.tab_params'), data: params},
      {type: 'description', label: t('product.tab_description'), data: description},
      {type: 'schema', label: t('product.tab_schema'), data: schema},
    ];
  }, [product]);

  return (
    <Component>
      {dataList.map((list, index) => {
        return !!list.data.length ? <Collapse {...list} key={index}/> : null;
      })}
    </Component>
  );
}

export default Tabs;

const Component = styled.div`
  margin-top: 24px;

  & > div .params-collapse-header {
    border-top: 1px solid ${colors.gray70};
  }

  & > div:last-child .params-collapse-header {
    border-bottom: 1px solid ${colors.gray70};
  }
`;
